/**
 * Offline Database Schema using Dexie.js (IndexedDB wrapper)
 * 
 * This provides local storage for offline-first functionality.
 * Data is synced with the server when online.
 */

import Dexie, { type Table } from 'dexie'

// ============================================
// Entity Types (matching server schema)
// ============================================

export interface OfflineBooking {
    id: string
    name: string
    tourCode?: string
    status: string
    startDate?: Date
    endDate?: Date
    paxCount?: number
    leadPassenger?: string
    totalRevenue?: number
    totalCost?: number
    destination?: string
    salesPersonId?: string
    salesPersonName?: string
    updatedAt: Date
    // Sync metadata
    _syncStatus: 'synced' | 'pending' | 'conflict'
    _localUpdatedAt: Date
}

export interface OfflinePassenger {
    id: string
    bookingId: string
    firstName: string
    lastName: string
    title?: string
    passport?: string
    nationality?: string
    dateOfBirth?: Date
    roomType: string
    email?: string
    phone?: string
    notes?: string
    updatedAt: Date
    // Sync metadata
    _syncStatus: 'synced' | 'pending' | 'conflict'
    _localUpdatedAt: Date
}

export interface OfflineItineraryDay {
    id: string
    bookingId: string
    dayNumber: number
    date?: Date
    title: string
    description?: string
    meals?: string
    accommodation?: string
    updatedAt: Date
    // Sync metadata
    _syncStatus: 'synced' | 'pending' | 'conflict'
    _localUpdatedAt: Date
}

export interface OfflineSupplier {
    id: string
    name: string
    category: string
    contactName?: string
    email?: string
    phone?: string
    address?: string
    updatedAt: Date
    // Sync metadata (suppliers are usually read-only offline)
    _syncStatus: 'synced'
    _localUpdatedAt: Date
}

export interface OfflineConfirmation {
    id: string
    bookingId: string
    supplierId: string
    supplierName: string
    serviceType: string
    serviceName: string
    status: string
    confirmationNumber?: string
    checkIn?: Date
    checkOut?: Date
    paxCount?: number
    totalCost?: number
    notes?: string
    updatedAt: Date
    // Sync metadata
    _syncStatus: 'synced' | 'pending' | 'conflict'
    _localUpdatedAt: Date
}

// ============================================
// Sync Queue for pending operations
// ============================================

export interface SyncOperation {
    id?: number  // Auto-increment
    entity: 'booking' | 'passenger' | 'itinerary' | 'confirmation'
    entityId: string
    action: 'create' | 'update' | 'delete'
    payload: any
    createdAt: Date
    status: 'pending' | 'syncing' | 'failed'
    retryCount: number
    errorMessage?: string
}

// ============================================
// Sync State tracking
// ============================================

export interface SyncState {
    entity: string  // Primary key
    lastSyncAt: Date
    syncInProgress: boolean
}

// ============================================
// Database Class
// ============================================

export class TourOpsOfflineDB extends Dexie {
    bookings!: Table<OfflineBooking, string>
    passengers!: Table<OfflinePassenger, string>
    itineraryDays!: Table<OfflineItineraryDay, string>
    suppliers!: Table<OfflineSupplier, string>
    confirmations!: Table<OfflineConfirmation, string>
    syncQueue!: Table<SyncOperation, number>
    syncState!: Table<SyncState, string>

    constructor() {
        super('TourOpsOfflineDB')

        this.version(1).stores({
            // Primary key, then indexed fields
            bookings: 'id, status, startDate, salesPersonId, _syncStatus',
            passengers: 'id, bookingId, _syncStatus',
            itineraryDays: 'id, bookingId, dayNumber, _syncStatus',
            suppliers: 'id, category, name',
            confirmations: 'id, bookingId, supplierId, status, _syncStatus',
            syncQueue: '++id, entity, entityId, status, createdAt',
            syncState: 'entity'
        })
    }
}

// Singleton instance
export const offlineDB = new TourOpsOfflineDB()

// ============================================
// Helper Functions
// ============================================

/**
 * Check if running in browser (not SSR)
 */
export function isClient(): boolean {
    return typeof window !== 'undefined' && typeof indexedDB !== 'undefined'
}

/**
 * Check online status
 */
export function isOnline(): boolean {
    if (!isClient()) return true
    return navigator.onLine
}

/**
 * Get pending sync count
 */
export async function getPendingSyncCount(): Promise<number> {
    if (!isClient()) return 0
    return offlineDB.syncQueue.where('status').equals('pending').count()
}

/**
 * Clear all offline data (for logout)
 */
export async function clearOfflineData(): Promise<void> {
    if (!isClient()) return
    await offlineDB.bookings.clear()
    await offlineDB.passengers.clear()
    await offlineDB.itineraryDays.clear()
    await offlineDB.confirmations.clear()
    await offlineDB.syncQueue.clear()
    await offlineDB.syncState.clear()
    // Keep suppliers as they're shared
}
